import React, { useState } from "react";
import axios from "axios";
import Reveal from "./Reveal";
import { toast } from "sonner";
import { Mail, MapPin, Linkedin, Twitter, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Ryan will be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-40 border-t border-gold-deep/40 z-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline mb-6">Contact · Work With Ryan</div>
              <h2 className="heading-section text-5xl lg:text-7xl text-white mb-8 leading-[0.95]">
                Let's build <span className="italic gold-gradient-text">what's next.</span>
              </h2>
              <p className="body-copy text-base max-w-md">
                Pitch an idea, partner on a project, or just say hello. Real messages get real
                replies — usually within a few days.
              </p>
            </Reveal>

            <Reveal delay={150}>
              <div className="mt-12 space-y-6">
                <a
                  href="mailto:ryan@ryanbest.com"
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <Mail className="w-4 h-4 mt-1 text-gold shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      Email
                    </div>
                    <div className="font-serif text-xl text-white group-hover:text-gold transition-colors">
                      ryan@ryanbest.com
                    </div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <MapPin className="w-4 h-4 mt-1 text-gold shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      Based in
                    </div>
                    <div className="font-serif text-xl text-white">Highland, Utah</div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="mt-12 pt-8 border-t border-gold-deep/50 flex items-center gap-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                  Elsewhere
                </span>
                <a
                  href="https://www.linkedin.com/in/mrryanbest/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-gold transition-colors"
                  data-testid="contact-social-linkedin"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com/MrRyanBest"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-gold transition-colors"
                  data-testid="contact-social-twitter"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://t.me/rybest"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-gold transition-colors"
                  data-testid="contact-social-telegram"
                  aria-label="Telegram"
                >
                  <Send className="w-5 h-5" />
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={150}>
              <form
                onSubmit={submit}
                className="relative p-8 lg:p-12 border border-gold-deep/70 bg-[#080706]"
                data-testid="contact-form"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 border-t border-l border-gold" />
                <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b border-r border-gold" />

                <div className="grid sm:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 block mb-3">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className="editorial-input"
                      placeholder="First & last"
                      required
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 block mb-3">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="editorial-input"
                      placeholder="you@example.com"
                      required
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="mb-8">
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 block mb-3">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="editorial-input"
                    placeholder="Optional"
                    data-testid="contact-input-phone"
                  />
                </div>

                <div className="mb-10">
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 block mb-3">
                    Message *
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    className="editorial-input min-h-[140px] resize-y"
                    placeholder="What are you building? What do you need?"
                    required
                    data-testid="contact-input-message"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 max-w-xs">
                    Your note goes straight to Ryan. No forms, no funnels.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {loading ? "Sending…" : "Send Message"}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
